// Talking to the Kinesin PCRS Client browser extension. Every PCRS call
// (scheme check, CDM patient details) goes through here.

import { PCRS_EXTENSION_ID } from "features/pcrsExtension/constants/pcrsExtension";

// Chrome only gives a page `chrome.runtime` when an installed extension lists
// the page in its externally_connectable — and only when the page loads. So
// it is missing before the extension is installed, and stays missing in an
// already-open tab until that tab reloads.
export const isExtensionMessagingAvailable = () => !!window.chrome?.runtime?.sendMessage;

// Send a message to the extension and resolve with its reply. Rejects when
// the extension is not reachable (not installed / wrong id).
export const sendToExtension = (message) =>
  new Promise((resolve, reject) => {
    if (!isExtensionMessagingAvailable()) {
      reject(new Error("PCRS extension not reachable — is the Kinesin PCRS Client installed?"));

      return;
    }

    window.chrome.runtime.sendMessage(PCRS_EXTENSION_ID, message, (response) => {
      const lastError = window.chrome.runtime.lastError;
      if (lastError) {
        reject(new Error(lastError.message));

        return;
      }

      resolve(response);
    });
  });

// Asks the extension to answer before any PCRS call is made, so a missing
// extension shows install instructions instead of a failed check.
export const isPcrsExtensionInstalled = async () => {
  try {
    const response = await sendToExtension({ operation: "__ping" });

    return response?.pong === true;
  } catch (error) {
    console.warn("[PCRS extension] not installed or not reachable:", error.message);

    return false;
  }
};

// The PCRS user-admin endpoint requires a client certificate (mTLS). The
// browser only shows the certificate picker for requests tied to a tab, not for
// the ones the extension's service worker makes — so without this the picker
// never appears and the extension's PCRS calls silently fail. Issuing a request
// to the endpoint from the page context surfaces the picker on entry; the chosen
// certificate is cached for the origin for the browser session and reused by the
// extension. no-cors keeps this a handshake only — the opaque response is
// ignored, and any failure is expected and swallowed. Resolves once the
// browser is done (including any certificate picker), so a retry can wait.
export const warmUpPcrsCertificate = async () => {
  // Dev-only: when the whole PCRS integration is mocked, skip the certificate
  // handshake — otherwise the browser's mTLS certificate picker still pops up
  // even though no real PCRS call is ever made.
  if (process.env.REACT_APP_CDM_MOCK_PCRS === "true") return;

  const endpoint = process.env.REACT_APP_SSPCRS_USER_ADMIN_ENDPOINT;
  if (!endpoint) return;

  await fetch(endpoint, { mode: "no-cors", credentials: "include" }).catch(() => {});
};
